import deepmerge from 'deepmerge/index.js';
/**
 * Merges two `MetaData` objects into a single object.
 *
 * If both `source` and `target` are undefined, returns `undefined`.
 * Otherwise, performs a deep merge of the two objects, with properties from `target` overriding those in `source`.
 *
 * @param source - The base `MetaData` object to merge from.
 * @param target - The `MetaData` object whose properties will override those in `source`.
 * @returns The merged `MetaData` object, or `undefined` if both inputs are undefined.
 */
export const mergeMetadata = (source, target) => {
    // Nothing to merge when neither side has metadata
    if (!source && !target) {
        return undefined;
    }
    return deepmerge(source ?? {}, target ?? {});
};
/**
 * Merges two partial `TelemetryItem` objects into a single `TelemetryItem`.
 *
 * @remarks
 * - Throws an error if either `target` or `source` is not defined.
 * - Throws an error if both `target.type` and `source.type` are defined and do not match.
 * - Combines the `scope` arrays from both objects, removing duplicates.
 * - Merges the `metadata` properties using the `mergeMetadata` function.
 * - Properties from `source` override those in `target` where applicable.
 *
 * @param target - The base partial `TelemetryItem` to merge into.
 * @param source - The partial `TelemetryItem` whose properties will override those in `target`.
 * @returns The merged `TelemetryItem`.
 * @throws {Error} If either argument is undefined or if telemetry item types mismatch.
 *
 * @internal
 */
// Deliberately co-located with `mergeMetadata` above
// fusion-lint-disable-next-line single-export-per-file
export const mergeTelemetryItem = (target, source) => {
    // Both arguments are required to produce a valid merged telemetry item
    if (!target || !source) {
        throw new Error('Both target and source must be defined for merging telemetry items.');
    }
    // A type mismatch would produce an inconsistent telemetry item, so fail fast
    if (target.type && source.type && target.type !== source.type) {
        throw new Error('Mismatched telemetry item types.');
    }
    // Combine both scopes and de-duplicate via a Set before flattening back to an array
    const scope = [...new Set([...(target.scope ?? []), ...(source.scope ?? [])])];
    const metadata = mergeMetadata(target.metadata, source.metadata);
    // Source properties take precedence over target, with scope/metadata merged separately above
    return { ...target, ...source, scope, metadata };
};
//# sourceMappingURL=merge-telemetry-item.js.map